import { BillingPeriod } from '@deathstar/types/northstar';
import { PaymentMethodInfo } from "@deathstar/types/waypoint";
import { SetupIntent } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/api";
import { useAccountId } from "../../../api/useAccountId";

export function useBillingDetails({ policyId, year, month }: { policyId: string; year: number; month: number }) {
    const accountId = useAccountId();
    const [isUpdating, setIsUpdating] = useState(false);

    const { data: billingSettings, isLoading: isLoadingBillingSettings } = api.policies.useFindBillingSettings(accountId!, policyId!, {
        enabled: !!accountId && !!policyId,
    });
    const { data: billingPeriods, isLoading: isLoadingBillingPeriods } = api.policies.useFindBillingPeriods(accountId!, policyId!, {
        enabled: !!accountId && !!policyId,
    });

    const {
        data: billingInfo,
        isLoading: isLoadingBillingInfo,
        refetch: refetchBillingProfile,
    } = api.billing.useProfile(accountId!, {
        enabled: !!accountId && !!policyId,
    });
    const { options, existingPaymentMethodIds } = useMemo(() => {
        const set = new Set<string>();
        const _options: PaymentMethodInfo[] = [];

        _options.push({
            id: "none",
            description: "None",
        } as PaymentMethodInfo);
        billingInfo?.paymentMethods?.forEach((pm) => {
            set.add(pm.id);
            _options.push(pm);
        });
        _options.push({
            id: "other",
            description: "other",
        } as PaymentMethodInfo);
        return {
            options: _options,
            existingPaymentMethodIds: set,
        };
    }, [billingInfo?.paymentMethods]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodInfo | null>(null);

    useEffect(() => {
        if (isLoadingBillingSettings || isLoadingBillingInfo || !billingSettings || !billingInfo) return;
        const defaultPaymentMethod = billingInfo?.paymentMethods.find((pm) => pm.id === billingSettings?.paymentMethodId);
        setSelectedPaymentMethod(defaultPaymentMethod || null);
    }, [billingInfo, billingSettings, isLoadingBillingInfo, isLoadingBillingSettings]);

    const onChangePaymentMethod = useCallback(
        async (paymentMethod: PaymentMethodInfo | null) => {
            setIsUpdating(true);
            await api.policies.updateBillingSettings(accountId!, policyId, {
                paymentMethodId: paymentMethod?.id === "none" ? null : paymentMethod?.id,
            });
            setSelectedPaymentMethod(paymentMethod?.id === "none" ? null : paymentMethod);
            setIsUpdating(false);
        },
        [accountId, policyId]
    );
    const targetBillingPeriod = useMemo(() => {
        return BillingPeriod.getBillingPeriodForMonthAndYear({
            billingPeriods: billingPeriods || [],
            month: month,
            year: year,
        });
    }, [billingPeriods, month, year]);

    return {
        policyId,
        paymentMethodOptions: options,
        onChangePaymentMethod,
        onAddPaymentMethod: async (setupIntent: SetupIntent | null) => {
            if (setupIntent) {
                setIsUpdating(true);
                const { data: billingInfo } = await refetchBillingProfile();
                const newPaymentMethod = billingInfo?.paymentMethods.find((pm) => {
                    if (!existingPaymentMethodIds.has(pm.id)) return pm;
                    return null;
                });
                if (newPaymentMethod) {
                    onChangePaymentMethod(newPaymentMethod);
                }
                setIsUpdating(false);
            }
        },
        allowCCPaymentMethods: billingInfo?.allowCCPaymentMethods ?? false,
        selectedPaymentMethod,
        billingSettings,
        billingPeriods,
        targetBillingPeriod,
        billingInfo,
        isLoading: isLoadingBillingInfo || isLoadingBillingSettings || isLoadingBillingPeriods,
        isUpdating,
    };
}